import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useOutletContext } from 'react-router-dom';
import { auth, db } from './firebaseConfig';
import { useTranslation } from 'react-i18next';
import { collection, query, where, onSnapshot, getDocs, orderBy, limit } from 'firebase/firestore';
import { Bot, MessageSquare, X } from 'lucide-react';
import './LinkedInTheme.css';

function Welcome({ user, userProfile, teamMemberJds, createdJds }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const contextData = useOutletContext();
  const isMobileMenuOpen = contextData ? contextData.isMobileMenuOpen : false; // Get menu state
  const [simulationStatuses, setSimulationStatuses] = useState({});
  const [newsItems, setNewsItems] = useState([]);
  const [latestVtcId, setLatestVtcId] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [displayedText, setDisplayedText] = useState('');
  const [showFloatingButton, setShowFloatingButton] = useState(true);
  
  const streamingText = t('welcome.streamingText');

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const isHidden = sessionStorage.getItem('hideFloatingTwin') === 'true';
    if (isHidden) {
      setShowFloatingButton(false);
    }
  }, []);

  const handleCloseFloating = (e) => {
    e.stopPropagation();
    setShowFloatingButton(false);
    sessionStorage.setItem('hideFloatingTwin', 'true');
  };

  // Typing effect
  useEffect(() => {
    if (!isMobile) return;
    
    let index = 0;
    const intervalId = setInterval(() => {
      setDisplayedText(streamingText.slice(0, index + 1));
      index++;
      if (index === streamingText.length) {
        clearInterval(intervalId);
      }
    }, 30); // Faster typing speed

    return () => clearInterval(intervalId);
  }, [isMobile]);

  useEffect(() => {
    const fetchLatestVtc = async () => {
      if (user) {
        try {
          // Fetch all cards for user and sort in memory to avoid index requirements
          const q = query(
            collection(db, 'talentCards'),
            where('userId', '==', user.uid)
          );
          const querySnapshot = await getDocs(q);
          if (!querySnapshot.empty) {
            const docs = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            docs.sort((a, b) => {
                const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(0);
                const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(0);
                return dateB - dateA; // Descending
            });
            setLatestVtcId(docs[0].id);
          }
        } catch (error) {
          console.error("Error fetching latest VTC:", error);
        }
      }
    };
    fetchLatestVtc();
  }, [user]);

  useEffect(() => {
    // Fetch AI & Future of Work News
    const fetchNews = async () => {
      try {
        const RSS_URL = 'https://techcrunch.com/category/artificial-intelligence/feed/';
        const response = await fetch(`https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(RSS_URL)}`);
        const data = await response.json();
        
        if (data.status === 'ok') {
          const formattedNews = data.items.slice(0, 5).map(item => ({
            title: item.title,
            link: item.link,
            time: new Date(item.pubDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
            readers: 0 // Field kept for structure but not displayed
          }));
          setNewsItems(formattedNews);
        }
      } catch (error) {
        console.error("Error fetching news:", error);
        // Fallback if fetch fails
        setNewsItems([
          { title: "The Future of AI in Recruitment", link: "#", time: "Today" },
          { title: "How AI Twins are Changing Work", link: "#", time: "Yesterday" },
          { title: "Automating the Candidate Journey", link: "#", time: "2d ago" }
        ]);
      }
    };

    fetchNews();
  }, []);

  useEffect(() => {
    if (!createdJds || createdJds.length === 0) {
      return;
    }

    const unsubs = [];
    createdJds.forEach(jd => {
      const q = query(collection(db, 'simulations'), where('jd_id', '==', jd.id));
      const unsub = onSnapshot(q, (snapshot) => {
        if (!snapshot.empty) {
          const simData = snapshot.docs[0].data();
          setSimulationStatuses(prev => ({ ...prev, [jd.id]: simData.status }));
        }
      });
      unsubs.push(unsub);
    });

    return () => unsubs.forEach(unsub => unsub());
  }, [createdJds]);

  const handleBuildCardClick = () => {
    localStorage.removeItem('sessionId');
    navigate('/chat');
  };

  // --- Data Generation for Action Cards ---
  const getActionItems = () => {
    const roles = userProfile?.roles || ['candidate'];
    const items = [];

    // 1. Build AI Twin
    items.push({
      key: 'build',
      icon: <svg fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M12 5v14m-7-7h14"/></svg>,
      title: t('welcome.buildCardTitle'),
      subtitle: t('welcome.buildCardSubtitle'),
      onClick: handleBuildCardClick
    });

    // 2. Simulations
    items.push({
      key: 'simulations',
      icon: <svg fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M5 3l14 9-14 9V3z"/></svg>,
      title: t('welcome.simulationsTitle'),
      subtitle: t('welcome.simulationsSubtitle'),
      onClick: () => navigate('/simulations')
    });

    // 3. Interviews / Reports
    items.push({
      key: 'interviews',
      icon: <svg fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5.586a1 1 0 0 1 .707.293l5.414 5.414a1 1 0 0 1 .293.707V19a2 2 0 0 1-2 2Z" /></svg>,
      title: t('welcome.interviewReportsTitle'),
      subtitle: t('welcome.interviewReportsSubtitle'),
      onClick: () => navigate('/candidate-interviews')
    });

    // 4. Profile
    items.push({
      key: 'profile',
      icon: <svg fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
      title: t('welcome.profileTitle'),
      subtitle: t('welcome.profileSubtitle'),
      onClick: () => navigate('/profile')
    });

    // Hiring Manager
    if (roles.includes('hm')) {
      items.push({
        key: 'start-hiring',
        icon: <svg fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>,
        title: t('welcome.hmTitle'),
        subtitle: t('welcome.hmSubtitle'),
        onClick: () => navigate('/start-hiring')
      });
    }

    // HR
    if (roles.includes('hr')) {
      items.push({
        key: 'hr-dashboard',
        icon: <svg fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25A1.125 1.125 0 0 1 9.75 19.875V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" /></svg>,
        title: t('hrDashboard.title'),
        subtitle: t('hrDashboard.subtitle'),
        onClick: () => navigate('/hr-dashboard')
      });
    }

    // Sales
    if (roles.includes('sales')) {
      items.push({
        key: 'sales',
        icon: <svg fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" /></svg>,
        title: t('welcome.salesTitle'),
        subtitle: t('welcome.salesSubtitle'),
        onClick: () => navigate('/sales-dashboard')
      });
    }

    // Admin
    if (roles.includes('admin')) {
      items.push({
        key: 'admin',
        icon: <svg fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75" /></svg>,
        title: t('welcome.adminTitle'),
        subtitle: t('welcome.adminSubtitle'),
        onClick: () => navigate('/admin')
      });
    }

    // Team Member Tasks
    if (teamMemberJds && teamMemberJds.length > 0) {
      const currentUser = auth.currentUser;
      if (currentUser) {
        teamMemberJds.forEach(jd => {
          const teamMemberInfo = jd.teamMembers?.find(member => member.uid === currentUser.uid);
          if (teamMemberInfo && teamMemberInfo.status === 'pending_input') {
            items.push({
              key: `tm-task-${jd.id}`,
              icon: <svg fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0ZM4 19.235v-.11a6.375 6.375 0 0 1 12.75 0v.109A12.318 12.318 0 0 1 10.374 21c-2.331 0-4.512-.645-6.374-1.766Z" /></svg>,
              title: `${t('welcome.tmTitle')} "${jd.title}"`,
              subtitle: t('welcome.tmSubtitle'),
              onClick: () => navigate(`/team-member-tasks/${jd.id}`)
            });
          }
        });
      }
    }

    return items;
  };

  const handleFloatingButtonClick = () => {
      if (latestVtcId) {
          navigate(`/digital-twin/${latestVtcId}`);
      } else {
          // Start a new chat to build a VTC if none exists
          localStorage.removeItem('sessionId');
          navigate('/chat');
      }
  };

  const handleInviteClick = () => {
    const roles = userProfile?.roles || [];
    if (roles.includes('admin') || roles.includes('hr')) {
      navigate('/invite-employees');
    } else {
      navigate('/organization-onboarding');
    }
  };

  const actionItems = getActionItems();

  return (
    <div className="li-layout">
      <div className="li-container">
        
        {/* Left Sidebar: Profile */}
        <aside className="li-sidebar-left">
           <div className="li-card li-profile-card">
              <div className="li-card-background"></div>
              <div className="li-card-content">
                 <img 
                    src={user?.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(userProfile?.displayName || user?.email || 'User')}&background=random`} 
                    alt="Profile" 
                    className="li-avatar" 
                    onClick={() => navigate('/profile')}
                 />
                 <div className="li-identity">
                    <h3 className="li-name" onClick={() => navigate('/profile')} style={{cursor: 'pointer'}}>
                        {userProfile?.displayName || user?.email?.split('@')[0]}
                    </h3>
                    <p className="li-headline">{userProfile?.headline || t('welcome.subtitle')}</p>
                 </div>
              </div>
           </div>

           {/* HM Active Jobs List (Left Sidebar) */}
           {createdJds && createdJds.length > 0 && (
               <div className="li-card">
                  <div className="li-card-header" style={{ padding: '12px 12px 4px', backgroundColor: '#eef3f8' }}>
                      <h3 style={{ fontSize: '14px', color: '#000000' }}>{t('welcome.hmJdListTitle')}</h3>
                  </div>
                  <ul className="li-list-groups">
                      {createdJds
                        .slice()
                        .sort((a, b) => {
                          const dateA = a.created_at?.toDate ? a.created_at.toDate() : new Date(0);
                          const dateB = b.created_at?.toDate ? b.created_at.toDate() : new Date(0);
                          return dateB - dateA;
                        })
                        .slice(0, 10)
                        .map(jd => {
                          const status = simulationStatuses[jd.id];
                          return (
                              <li key={jd.id} onClick={() => navigate(`/hm-tasks/${jd.id}`)} style={{ padding: '8px 12px' }}>
                                  <div style={{ flex: 1 }}>
                                      <div style={{ fontWeight: '600', fontSize: '13px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{jd.title}</div>
                                      <div style={{ fontSize: '11px', color: 'rgba(0,0,0,0.6)' }}>
                                          {status ? t(`status.${status}`, status) : t('status.readyForLaunch', 'Ready for launch')}
                                      </div>
                                  </div>
                              </li>
                          );
                      })}
                  </ul>
               </div>
           )}

           {/* Invite Promo Card (Moved to Left) */}
           <div className="li-card li-promo-card">
               <div style={{ fontSize: '12px', color: 'rgba(0,0,0,0.6)', marginBottom: '12px', textAlign: 'left' }}>
                   {t('welcome.promo.growOrgTitle')}
               </div>
               <div className="li-ad-image-placeholder" style={{ padding: 0, overflow: 'hidden', backgroundColor: 'transparent', height: '120px', marginBottom: '12px' }}>
                   <img 
                     src={process.env.PUBLIC_URL + "/Flat_illutrations/cozy_living_room.png"} 
                     alt={t('welcome.promo.inviteDesc')} 
                     style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                   />
               </div>
               <div style={{ fontSize: '14px', fontWeight: '600', margin: '0 0 8px' }}>
                   {t('welcome.promo.bringTeamTitle')}
               </div>
               <div style={{ fontSize: '12px', color: 'rgba(0,0,0,0.9)', marginBottom: '12px' }}>
                   {t('welcome.promo.inviteDesc')}
               </div>
               <button className="li-btn-outline-primary" onClick={handleInviteClick}>{t('welcome.promo.inviteButton')}</button>
           </div>




        </aside>

        {/* Main Feed: Dashboard Grid */}
        <main className="li-main-feed">
           
           {/* Welcome Message Card */}
           <div className="li-card hide-on-mobile" style={{ padding: '16px' }}>
               <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '8px', color: 'rgba(0,0,0,0.9)' }}>
                   {t('welcome.createTwinTitle')}
               </h2>
               <p style={{ fontSize: '14px', color: 'rgba(0,0,0,0.6)' }}>
                   {t('welcome.createTwinSubtitle', 'Build your personal AI agent to work for you 24/7.')}
               </p>
           </div>

           {/* Action Dashboard Grid */}
           <div className="li-dashboard-grid">
              {actionItems.map(item => (
                <div key={item.key} className="li-dashboard-card" onClick={item.onClick}>
                   <div className="li-dashboard-icon">{item.icon}</div>
                   <h3 className="li-dashboard-title">{item.title}</h3>
                   <p className="li-dashboard-subtitle">{item.subtitle}</p>
                </div>
              ))}
           </div>

        </main>

        {/* Right Sidebar: News / Promos */}
        <aside className="li-sidebar-right">
           {i18n.language.startsWith('tr') ? (
             <div className="li-card" style={{ padding: 0, overflow: 'hidden', marginBottom: '16px' }}>
                 <img src={process.env.PUBLIC_URL + "/Flat_illutrations/interview.png"} alt="POD+ İş bulma ve işe yerleştirme platformu" style={{ width: '100%', height: 'auto', display: 'block' }} />
             </div>
           ) : (
             <>
               <div className="li-card" style={{ padding: 0, overflow: 'hidden', marginBottom: '16px' }}>
                   <img src={process.env.PUBLIC_URL + "/pod-banner-en.jpg"} alt="POD+ All-in-One Talent Acquisition" style={{ width: '100%', height: 'auto', display: 'block' }} />
               </div>
               <div className="li-card li-news-card hide-on-mobile">
                 <div className="li-card-header" style={{ backgroundColor: '#eef3f8' }}>
                     <h3 style={{ color: '#000000' }}>{t('welcome.podNewsTitle')}</h3>
                 </div>
                 <div className="li-card-body">
                     <ul className="li-entity-list" style={{ borderTop: 'none' }}>
                         {newsItems.length > 0 ? newsItems.map((item, index) => (
                           <li className="li-entity-item" key={index} onClick={() => window.open(item.link, '_blank')}>
                               <div className="li-entity-label">
                                   <div style={{ fontWeight: '600', color: 'rgba(0,0,0,0.9)', fontSize: '13px', lineHeight: '1.4', marginBottom: '4px' }}>
                                     {item.title}
                                   </div>
                                   <div style={{ fontSize: '12px', color: 'rgba(0,0,0,0.6)' }}>
                                     {item.time}
                                   </div>
                               </div>
                           </li>
                         )) : (
                           <li className="li-entity-item">
                             <div className="li-entity-label">
                               <div style={{ fontSize: '12px', color: 'rgba(0,0,0,0.6)' }}>Loading news...</div>
                             </div>
                           </li>
                         )}
                     </ul>
                 </div>
             </div>
             </>
           )}
           
           <div className="li-footer-links">
             <Link to="/about">{t('footer.about')}</Link>
             <Link to="/accessibility">{t('footer.accessibility')}</Link>
             <Link to="/help-center">{t('footer.helpCenter')}</Link>
             <Link to="/privacy-terms">{t('footer.privacyTerms')}</Link>
             <span>{t('auth.footer.rights')}</span>
           </div>

        </aside>

      </div>

      {/* Floating AI Twin Button (Mobile Only) - Hide when mobile menu is open */}
      {isMobile && !isMobileMenuOpen && showFloatingButton && (
        <>
          {/* Close Button */}
          <button
            onClick={handleCloseFloating}
            style={{
              position: 'fixed',
              bottom: '130px', // positioned between bubble and main button
              right: '10px',
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              color: 'white',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1003,
              cursor: 'pointer',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}
          >
            <X size={14} />
          </button>

          {/* Speech Bubble */}
          <div style={{
            position: 'fixed',
            bottom: '145px',
            right: '20px',
            maxWidth: '250px',
            backgroundColor: 'white',
            padding: '12px',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            fontSize: '13px',
            color: '#333',
            lineHeight: '1.4',
            zIndex: 1002,
            animation: 'fadeIn 0.5s ease-in-out'
          }}>
            {displayedText}
            {/* Arrow */}
            <div style={{
              position: 'absolute',
              bottom: '-8px',
              right: '20px',
              width: '0',
              height: '0',
              borderLeft: '8px solid transparent',
              borderRight: '8px solid transparent',
              borderTop: '8px solid white'
            }}></div>
          </div>

          <button
            onClick={handleFloatingButtonClick}
          style={{
            position: 'fixed',
            bottom: '80px',
            right: '20px',
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            backgroundColor: '#0a66c2',
            color: 'white',
            border: 'none',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1002,
            cursor: 'pointer'
          }}
        >
          <Bot size={28} />
        </button>
        </>
      )}
    </div>
  );
}

export default Welcome;
